import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Search } from "lucide-react";
import { DIVISIONS } from "../../lib/api";
import { Reveal } from "./Motion";
import { SourcingForm } from "./SourcingForm";

export const AvailableBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-2 border border-[rgba(212,175,55,0.45)] px-3 py-1.5 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[#e9cf7a] ${className}`} data-testid="available-upon-request-badge">
    <span className="w-1.5 h-1.5 rotate-45 bg-[var(--gold)]" /> Available Upon Request
  </span>
);

export const scrollToRequest = () => document.getElementById("request-form")?.scrollIntoView({ behavior: "smooth", block: "start" });

const DivisionPicker = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border hairline" data-testid="sourcing-division-picker">
    {DIVISIONS.map((d) => (
      <Link key={d.key} to={`/divisions/${d.key}`} className="group relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden bg-[var(--ink)]" data-testid={`sourcing-division-card-${d.key}`}>
        <img src={d.img} alt={d.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-[opacity,transform] duration-700" />
        <span className="absolute inset-0 bg-gradient-to-t from-[#0a0d14] via-[#0a0d14]/40 to-transparent" />
        <span className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-2">
          <span>
            <span className="block font-display text-2xl md:text-3xl text-slate-50 leading-none">{d.short}</span>
            <span className="block text-xs text-slate-300 mt-1.5">{d.categories.length} asset types</span>
          </span>
          <ArrowUpRight className="w-5 h-5 text-gold shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </span>
      </Link>
    ))}
  </div>
);

export const SourcingHub = ({ hasListings = false }) => (
  <section className="space-y-14" data-testid="sourcing-hub">
    <Reveal className="grid lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-7">
        <AvailableBadge />
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1] mt-6" data-testid="sourcing-hub-title">
          {hasListings ? <>Don't see it here? <em className="text-gold">We'll help you source it.</em></> : <>Find the Right Asset. <em className="text-gold">We'll Help You Source It.</em></>}
        </h2>
      </div>
      <div className="lg:col-span-5 space-y-6">
        <p className="text-slate-300 leading-relaxed">
          Selby Asset Group sources trucks, trailers, boats and yachts, and construction and industrial equipment around your needs, specifications and budget. Tell us what you're looking for and we'll search relevant channels and business relationships for suitable opportunities, keeping you informed at every step.
        </p>
        <button type="button" onClick={scrollToRequest} className="btn-gold" data-testid="sourcing-hub-request-button"><Search className="w-4 h-4" /> Request an Asset</button>
      </div>
    </Reveal>
    <div>
      <p className="eyebrow mb-5">Browse by division</p>
      <DivisionPicker />
    </div>
    <div id="request-form" className="scroll-mt-28 grid lg:grid-cols-12 gap-10 border-t hairline pt-14">
      <div className="lg:col-span-4 space-y-4">
        <p className="eyebrow">Sourcing request</p>
        <p className="font-display text-3xl text-slate-50 leading-tight">Tell us what you need.</p>
        <p className="text-sm text-slate-400 leading-relaxed">Requests go straight to our team and are followed up personally. Sourcing is subject to market availability and confirmation.</p>
      </div>
      <div className="lg:col-span-8"><SourcingForm scrollOnDone={false} /></div>
    </div>
  </section>
);

export const AssetTypeGrid = ({ division, selected, onSelect }) => (
  <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border hairline" data-testid={`asset-types-${division.key}`}>
    {division.categories.map((c, i) => {
      const active = selected === c;
      return (
        <li key={c} className="bg-[var(--ink)]">
          <button type="button" onClick={() => onSelect(c)} aria-pressed={active}
            className={`group w-full h-full text-left p-6 min-h-[150px] flex flex-col justify-between gap-6 transition-colors duration-300 ${active ? "bg-[rgba(212,175,55,0.1)]" : "hover:bg-white/[0.03]"}`}
            data-testid={`asset-type-${division.key}-${i}`}>
            <span className="flex items-start justify-between gap-3">
              <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
              <span className={`text-[0.65rem] font-mono uppercase tracking-[0.14em] ${active ? "text-gold" : "text-slate-400"}`}>{active ? "Selected" : "Upon request"}</span>
            </span>
            <span>
              <span className="block font-display text-2xl text-slate-50 leading-tight">{c}</span>
              <span className={`mt-3 inline-flex items-center gap-1.5 text-xs ${active ? "text-gold" : "text-slate-300 group-hover:text-gold"} transition-colors`}>Request this asset <ArrowRight className="w-3 h-3" /></span>
            </span>
          </button>
        </li>
      );
    })}
  </ul>
);
